/**
 * Customer-facing HR order code + passenger line for flights_new bank-transfer success.
 * Never expose UUID / LDS deal tokens — same resolver as other LDS success pages.
 */
import {
  formatHrLabel,
  isLikelyOpaqueLdsDealToken,
} from "../resolveCustomerFacingOfferNumber.js";
import { resolveLdsSuccessDisplayOrderCode } from "../resolveLdsSuccessDisplayOrderCode.js";
import { resolveVacationPackageIncludedBaggageForLeg } from "../resolveVacationPackageIncludedBaggage.js";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function trimStr(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v).trim();
  if (typeof v === "string") return v.trim();
  return "";
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    const t = trimStr(v);
    if (t) return t;
  }
  return "";
}

/**
 * @param {object|null|undefined} orderData
 * @param {object|null|undefined} dynamicData
 * @returns {string}
 */
export function resolveFlightsSuccessHrOrderCode(orderData, dynamicData) {
  const flights = dynamicData?.flights || orderData?.flights || {};
  const merged = {
    ...(dynamicData && typeof dynamicData === "object" ? dynamicData : {}),
    ...(orderData && typeof orderData === "object" ? orderData : {}),
    hr_order_number: firstNonEmpty(
      orderData?.hr_order_number,
      orderData?.hrOrderNumber,
      dynamicData?.hr_order_number,
      dynamicData?.hrOrderNumber
    ),
    booking_ref: firstNonEmpty(
      orderData?.booking_ref,
      orderData?.bookingRef,
      dynamicData?.booking_ref,
      dynamicData?.bookingRef,
      flights?.booking_ref,
      flights?.bookingRef
    ),
    booking_ref_display: firstNonEmpty(
      orderData?.booking_ref_display,
      orderData?.bookingRefDisplay,
      dynamicData?.booking_ref_display,
      dynamicData?.bookingRefDisplay,
      flights?.booking_ref_display
    ),
  };
  const tech = firstNonEmpty(
    orderData?.id,
    dynamicData?.id,
    orderData?.ldsorderid,
    dynamicData?.ldsorderid
  );
  const code = resolveLdsSuccessDisplayOrderCode(merged, tech || null).selectedDisplayCode;
  if (UUID_RE.test(String(code || ""))) {
    return resolveLdsSuccessDisplayOrderCode({}, tech || "order").selectedDisplayCode;
  }
  return code;
}

/**
 * Bank-transfer instructions: the stored HR / system order number.
 * Does not fall back to a generated code, and never returns a UUID.
 * @param {object|null|undefined} orderData
 * @param {object|null|undefined} dynamicData
 * @returns {string}
 */
export function resolveFlightsBankTransferOrderNumber(orderData, dynamicData = null) {
  const code = String(resolveFlightsSuccessHrOrderCode(orderData, dynamicData) || "").trim();
  if (!code || UUID_RE.test(code)) return "";
  const tech = firstNonEmpty(
    orderData?.id,
    dynamicData?.id,
    orderData?.ldsorderid,
    dynamicData?.ldsorderid
  );
  if (tech && UUID_RE.test(tech)) {
    const generated = resolveLdsSuccessDisplayOrderCode({}, tech).selectedDisplayCode;
    if (code === generated) return "";
  }
  return code;
}

function countFromPassengersInfo(rooms) {
  if (!Array.isArray(rooms) || !rooms.length) return null;
  let adults = 0;
  let kids = 0;
  let infants = 0;
  for (const room of rooms) {
    if (!room || typeof room !== "object") continue;
    adults += Array.isArray(room.adults) ? room.adults.length : 0;
    kids += Array.isArray(room.kids) ? room.kids.length : 0;
    infants += Array.isArray(room.infants) ? room.infants.length : 0;
  }
  if (adults + kids + infants <= 0) return null;
  return { adults, kids, infants };
}

function countFromGimmonixPassengers(raw) {
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(list) || !list.length) return null;
  let adults = 0;
  let kids = 0;
  let infants = 0;
  for (const p of list) {
    const age = Number(p?.PersonDetails?.Age ?? p?.age);
    if (Number.isFinite(age) && age < 2) infants += 1;
    else if (Number.isFinite(age) && age < 12) kids += 1;
    else adults += 1;
  }
  return { adults, kids, infants };
}

/**
 * @param {object|null|undefined} orderData
 * @param {object|null|undefined} dynamicData
 * @param {{ adults?: string, kids?: string, infants?: string }} labels
 */
export function formatFlightsSuccessPassengersLine(orderData, dynamicData, labels = {}) {
  const adultsLabel = labels.adults || "מבוגרים";
  const kidsLabel = labels.kids || "ילדים";
  const infantsLabel = labels.infants || "תינוקות";
  const counts =
    countFromPassengersInfo(dynamicData?.flights?.passengersinfo) ||
    countFromPassengersInfo(orderData?.flights?.passengersinfo) ||
    countFromGimmonixPassengers(orderData?.passengers);
  if (!counts) return "—";
  const parts = [];
  if (counts.adults) parts.push(`${counts.adults} ${adultsLabel}`);
  if (counts.kids) parts.push(`${counts.kids} ${kidsLabel}`);
  if (counts.infants) parts.push(`${counts.infants} ${infantsLabel}`);
  return parts.join(", ") || "—";
}

/**
 * @param {object|null|undefined} orderData
 * @param {object|null|undefined} dynamicData
 */
export function formatFlightsSuccessDatesLine(orderData, dynamicData) {
  const checkin = firstNonEmpty(
    orderData?.checkin,
    orderData?.check_in,
    dynamicData?.check_in,
    dynamicData?.checkin
  );
  const checkout = firstNonEmpty(
    orderData?.checkout,
    orderData?.check_out,
    dynamicData?.check_out,
    dynamicData?.checkout
  );
  if (!checkin && !checkout) return "—";
  if (checkin && checkout) return `${checkin} – ${checkout}`;
  return checkin || checkout;
}

function baggageTypeHe(type) {
  const t = String(type || "").toUpperCase();
  if (t === "TROLLEY") return "טרולי";
  if (t === "LUGGAGE" || t === "BAGGAGE") return "מזוודה";
  return "כבודה";
}

function paxDisplayName(pax) {
  if (!pax || typeof pax !== "object") return "";
  return firstNonEmpty(
    [pax.firstname, pax.lastname].filter(Boolean).join(" "),
    [pax.firstName, pax.lastName].filter(Boolean).join(" "),
    pax.fullname,
    pax.fullName
  );
}

function isTruthyBaggageFlag(value) {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return false;
  const t = value.trim().toLowerCase();
  return t === "true" || t === "1" || t === "yes" || t === "included";
}

function isNamedBaggageType(type) {
  const t = String(type || "").toUpperCase();
  return (
    t === "TROLLEY" ||
    t === "LUGGAGE" ||
    t === "BAGGAGE" ||
    t === "CHECKED" ||
    t === "CARRYON"
  );
}

function supplementIsFareIncluded(entry) {
  if (!entry || typeof entry !== "object") return false;
  const inner = entry.orig?.supp && typeof entry.orig.supp === "object" ? entry.orig.supp : entry;
  if (
    isTruthyBaggageFlag(entry.include) ||
    isTruthyBaggageFlag(entry.included) ||
    isTruthyBaggageFlag(inner.include) ||
    isTruthyBaggageFlag(inner.included)
  ) {
    return true;
  }
  const fare = Number(entry.ourFare ?? entry.fare ?? inner.fare ?? inner.ourFare);
  return Number.isFinite(fare) && fare === 0;
}

function parseMaybeLinkedObject(value) {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;
  const t = value.trim();
  if (!t.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(t);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function pickLinkedFlightsRoot(dynamicData, orderData) {
  const candidates = [
    dynamicData?.flights?.linkedflights,
    dynamicData?.flights?.linkedFlights,
    dynamicData?.linkedflights,
    dynamicData?.linkedFlights,
    orderData?.flights?.linkedflights,
    orderData?.flights?.linkedFlights,
    orderData?.linkedflights,
    orderData?.linkedFlights,
  ];
  for (const raw of candidates) {
    const parsed = parseMaybeLinkedObject(raw);
    if (parsed) return parsed;
  }
  return null;
}

function flightItemsFromLinkedRoot(lf) {
  if (!lf || typeof lf !== "object") return [];
  const inner =
    lf.linkedFlights && typeof lf.linkedFlights === "object" && !Array.isArray(lf.linkedFlights)
      ? lf.linkedFlights
      : lf;
  const raw = inner.flight ?? inner.flights;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === "object") return [raw];
  return [];
}

function unwrapFlightLeg(legLike) {
  if (legLike == null) return null;
  return Array.isArray(legLike) ? legLike[0] : legLike;
}

function formatIncludedWeightKg(kg) {
  if (kg == null) return "";
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${n}kg`;
}

function hasFareIncludedBaggageType(rows, type) {
  const want =
    String(type).toUpperCase() === "TROLLEY" ? ["TROLLEY"] : ["LUGGAGE", "BAGGAGE", "CHECKED"];
  return rows.some((row) => {
    const t = String(row?.type || "").toUpperCase();
    if (!want.includes(t)) return false;
    const price = Number(row?.priceIls);
    return row?.priceIls == null || !Number.isFinite(price) || price === 0;
  });
}

function appendSupplementBaggageRow(rows, entry, { forceIncluded = false } = {}) {
  const type = firstNonEmpty(entry.type, entry.orig?.supp?.type) || (forceIncluded ? "" : "BAGGAGE");
  if (forceIncluded && !isNamedBaggageType(type)) return;
  const weight = firstNonEmpty(
    entry.weightText,
    entry.orig?.supp?.weight != null
      ? `${entry.orig.supp.weight}${entry.orig.supp.unit || "kg"}`
      : ""
  );
  const priceIls = Number(entry.ourFare ?? entry.fare);
  const origFare = Number(entry.orig?.supp?.fare ?? entry.orig?.supp?.Fare);
  const origCur = String(
    entry.orig?.supp?.currency ||
      entry.orig?.supp?.Currency ||
      entry.currency ||
      ""
  ).toUpperCase();
  const storedUsd = Number(entry.fareUsd ?? entry.ourFareUsd ?? entry.orig?.supp?.fareUsd);
  let priceUsd = null;
  if (!forceIncluded && Number.isFinite(storedUsd) && storedUsd > 0) {
    priceUsd = storedUsd;
  } else if (!forceIncluded && origCur === "USD" && Number.isFinite(origFare) && origFare > 0) {
    priceUsd = origFare;
  }
  const dep = firstNonEmpty(entry.flight?.depCity, entry.orig?.flight?.depCity);
  const arr = firstNonEmpty(entry.flight?.arrCity, entry.orig?.flight?.arrCity);
  rows.push({
    type: String(type).toUpperCase(),
    typeHe: baggageTypeHe(type),
    description: firstNonEmpty(entry.description, entry.orig?.supp?.description, baggageTypeHe(type)),
    weight,
    priceIls: forceIncluded
      ? null
      : Number.isFinite(priceIls) && priceIls > 0
        ? priceIls
        : Number.isFinite(origFare)
          ? origFare
          : null,
    priceUsd,
    passenger: paxDisplayName(entry.pax),
    direction: dep && arr ? `${dep} → ${arr}` : "",
    from: dep,
    to: arr,
  });
}

function bagPassengerCopies(dynamicData, orderData) {
  const counts =
    countFromPassengersInfo(dynamicData?.flights?.passengersinfo) ||
    countFromPassengersInfo(orderData?.flights?.passengersinfo) ||
    countFromPassengersInfo(dynamicData?.passengersinfo) ||
    countFromPassengersInfo(orderData?.passengersinfo);
  if (!counts) return 1;
  const n = (counts.adults || 0) + (counts.kids || 0);
  return n > 0 ? n : 1;
}

function mergeReservationFlightItems(items, dynamicData, orderData) {
  const bodies = collectOrderResponseBodies(orderData, dynamicData);
  for (const body of bodies) {
    const res = reservationFromBody(body);
    const resItems = flightItemsFromLinkedRoot({
      flight: res?.flight ?? res?.flights,
    });
    if (!resItems.length) continue;
    if (!items.length) return resItems;
    return items.map((item, i) => {
      const fb = resItems[i];
      if (!fb) return item;
      const leg = unwrapFlightLeg(item?.leg);
      const fbLeg = unwrapFlightLeg(fb?.leg ?? fb);
      if (!leg && !fbLeg) return item;
      return { ...item, leg: { ...(fbLeg || {}), ...(leg || {}) } };
    });
  }
  return items;
}

function appendIncludedBaggageFromLinkedFlights(rows, dynamicData, orderData) {
  const linked = pickLinkedFlightsRoot(dynamicData, orderData);
  let items = mergeReservationFlightItems(flightItemsFromLinkedRoot(linked), dynamicData, orderData);
  if (!items.length) return;

  const skipLuggage = hasFareIncludedBaggageType(rows, "LUGGAGE");
  const skipTrolley = hasFareIncludedBaggageType(rows, "TROLLEY");
  if (skipLuggage && skipTrolley) return;

  const copies = bagPassengerCopies(dynamicData, orderData);
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const leg = unwrapFlightLeg(item.leg);
    const included = resolveVacationPackageIncludedBaggageForLeg(leg, item);
    const dep = firstNonEmpty(
      leg?.depCity,
      leg?.depAirport?.code,
      item.depCity,
      item.depAirport?.code
    );
    const arr = firstNonEmpty(
      leg?.arrCity,
      leg?.arrAirport?.code,
      item.arrCity,
      item.arrAirport?.code
    );
    const direction = dep && arr ? `${dep} → ${arr}` : "";
    if (included.checkedBaggageIncluded && !skipLuggage) {
      const weight = formatIncludedWeightKg(included.checkedBaggageWeightKg);
      for (let i = 0; i < copies; i += 1) {
        rows.push({
          type: "LUGGAGE",
          typeHe: baggageTypeHe("LUGGAGE"),
          description: baggageTypeHe("LUGGAGE"),
          weight,
          priceIls: null,
          priceUsd: null,
          passenger: "",
          direction,
          from: dep,
          to: arr,
        });
      }
    }
    if (included.carryOnIncluded && !skipTrolley) {
      const weight = formatIncludedWeightKg(included.carryOnWeightKg);
      for (let i = 0; i < copies; i += 1) {
        rows.push({
          type: "TROLLEY",
          typeHe: baggageTypeHe("TROLLEY"),
          description: baggageTypeHe("TROLLEY"),
          weight,
          priceIls: null,
          priceUsd: null,
          passenger: "",
          direction,
          from: dep,
          to: arr,
        });
      }
    }
  }
}

/**
 * Paid extras from flights.supplement (checked === true) plus fare-included
 * trolley / suitcase from fare-0 supplements or linkedFlights allowances.
 * Included rows keep priceIls/priceUsd null so print never shows ₪.
 * @param {object|null|undefined} dynamicData
 * @param {object|null|undefined} orderData
 * @returns {{ type: string, typeHe: string, description: string, weight: string, priceIls: number|null, passenger: string, direction: string }[]}
 */
export function buildFlightsSuccessBaggageRows(dynamicData, orderData) {
  const supplement =
    dynamicData?.flights?.supplement ||
    orderData?.flights?.supplement ||
    dynamicData?.supplement ||
    orderData?.supplement ||
    {};
  const entries = Array.isArray(supplement) ? supplement : Object.values(supplement);
  const rows = [];
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.checked !== true) continue;
    appendSupplementBaggageRow(rows, entry);
  }
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.checked === true) continue;
    if (!supplementIsFareIncluded(entry)) continue;
    appendSupplementBaggageRow(rows, entry, { forceIncluded: true });
  }
  appendIncludedBaggageFromLinkedFlights(rows, dynamicData, orderData);
  return rows;
}

function parseJsonObject(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(t);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function collectOrderResponseBodies(orderData, dynamicData) {
  const bodies = [];
  const seen = new Set();
  for (const raw of [
    dynamicData?.flights?.order_response,
    orderData?.flights?.order_response,
    dynamicData?.order_response,
    orderData?.order_response,
  ]) {
    const parsed = parseJsonObject(raw);
    if (!parsed || seen.has(parsed)) continue;
    seen.add(parsed);
    bodies.push(parsed);
  }
  return bodies;
}

function reservationFromBody(body) {
  if (!body || typeof body !== "object") return null;
  const res = body.reservation;
  return res && typeof res === "object" && !Array.isArray(res) ? res : body;
}

function normalizeHrDigits(value) {
  const hr = formatHrLabel(value);
  if (hr) return hr.replace(/^HR-/i, "");
  const digits = trimStr(value).replace(/^hr[\s\-_:]*/i, "").replace(/\D/g, "");
  return digits || "";
}

function sameAsHrCode(value, hrOrderCode) {
  const a = normalizeHrDigits(value);
  const b = normalizeHrDigits(hrOrderCode);
  return Boolean(a && b && a === b);
}

function isForbiddenSupplierRef(value) {
  const t = trimStr(value);
  if (!t) return true;
  if (t.startsWith("eyJ")) return true;
  if (UUID_RE.test(t)) return true;
  if (isLikelyOpaqueLdsDealToken(t)) return true;
  if (t.length > 32) return true;
  if (!/^[A-Za-z0-9\-_/]+$/.test(t)) return true;
  return false;
}

function pickReadableSupplierRef(raw) {
  const t = trimStr(raw);
  if (!t || isForbiddenSupplierRef(t)) return "";
  if (t.length < 3) return "";
  return t;
}

/** Classic airline / Sabre record locator: 5–7 alphanumeric with a letter. */
export function isClassicAirlinePnr(value) {
  const t = trimStr(value).toUpperCase();
  if (!/^[A-Z0-9]{5,7}$/.test(t)) return false;
  return /[A-Z]/.test(t);
}

/**
 * Long numeric LDS/docket id (10–16 digits) — not a Sabre locator and not a
 * supplier/Kavei booking PNR. 7–9 digit numeric supplierPNR values (e.g. 51202826)
 * stay available as Airline PNR.
 */
export function isSupplierBookingReference(value) {
  const t = trimStr(value);
  if (!t) return false;
  return /^\d{10,16}$/.test(t);
}

/** Numeric supplier/Kavei PNR that is not a long LDS docket id. */
export function isNumericSupplierPnr(value) {
  const t = trimStr(value);
  if (!t || isSupplierBookingReference(t) || isClassicAirlinePnr(t)) return false;
  return /^\d{6,9}$/.test(t);
}

export function formatETicketNumber(raw) {
  const t = trimStr(raw);
  if (!t) return "";
  const digits = t.replace(/\D/g, "");
  if (digits.length === 13 || digits.length === 14) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (digits.length >= 10) return digits;
  return t;
}

function collectTicketCandidates(reservations, bodies, flights, orderData, dynamicData) {
  const out = [];
  const push = (raw) => {
    const formatted = formatETicketNumber(raw);
    if (formatted && !out.includes(formatted)) out.push(formatted);
  };
  for (const res of reservations) {
    push(res.ticketNumber);
    push(res.ticket_number);
    push(res.eTicketNumber);
    push(res.eTicket);
    push(res.eticket);
    push(res.TicketNumber);
    const lists = [res.tickets, res.eTickets, res.ticketNumbers, res.eTicketNumbers];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const item of list) {
        if (item == null) continue;
        if (typeof item === "string" || typeof item === "number") push(item);
        else push(item.ticketNumber || item.eTicketNumber || item.number || item.eticket);
      }
    }
  }
  for (const body of bodies) {
    push(body.ticketNumber);
    push(body.eTicketNumber);
    push(body.eTicket);
  }
  push(flights.ticketNumber);
  push(orderData?.ticketNumber);
  push(dynamicData?.ticketNumber);
  return out;
}

function firstReadableRef(candidates, hrOrderCode) {
  for (const raw of candidates) {
    const t = pickReadableSupplierRef(raw);
    if (t && !sameAsHrCode(t, hrOrderCode)) return t;
  }
  return "";
}

function leadPassengerFromRooms(rooms) {
  if (!Array.isArray(rooms)) return "";
  for (const room of rooms) {
    if (!room || typeof room !== "object") continue;
    const buckets = [room.adults, room.kids, room.infants, room.passengers];
    for (const bucket of buckets) {
      if (!Array.isArray(bucket)) continue;
      for (const pax of bucket) {
        const name = paxDisplayName(pax);
        if (name) return name;
      }
    }
  }
  return "";
}

function formatTicketIssueDate(raw) {
  const t = trimStr(raw);
  if (!t) return "";
  const datePart = t.split(/[T\s]/)[0];
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(datePart)) return datePart;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [y, m, d] = datePart.split("-");
    return `${d}/${m}/${y}`;
  }
  const parsed = new Date(t);
  if (Number.isNaN(parsed.getTime())) return "";
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${parsed.getFullYear()}`;
}

/**
 * El Al-style ticket header fields for סיכום הזמנה + PDF.
 * Booking code = real LDS supplier PNR/locator when present, otherwise the HR order number.
 * SABRE PNR is only a classic 5–7 char record locator.
 * Long numeric reservation.sabrePNR values (10–16 digits) are supplierBookingRef, never labeled SABRE PNR.
 * Numeric supplierPNR that is not a long docket (e.g. Kavei 8-digit 51202826) is Airline PNR.
 * Ticket / docket rows are omitted when empty — never invent airline codes.
 *
 * @param {object|null|undefined} orderData
 * @param {object|null|undefined} dynamicData
 * @param {{
 *   hrOrderCode?: string,
 *   passengerFallback?: string,
 *   issuingOffice?: string,
 *   telephone?: string,
 *   isAwaitingPayment?: boolean,
 * }} [opts]
 * @returns {{
 *   passenger: string,
 *   bookingCode: string,
 *   bookingCodeSource: "pnr"|"hr"|"",
 *   pnr: string,
 *   airlinePnr: string,
 *   sabrePnr: string,
 *   supplierBookingRef: string,
 *   docketId: string,
 *   ticketNumber: string,
 *   eTickets: string[],
 *   issuingOffice: string,
 *   telephone: string,
 *   date: string,
 *   statusLabel: string,
 *   statusTone: "itinerary"|"confirmed"|"ticketed",
 * }}
 */
export function resolveFlightsSuccessTicketHeader(orderData, dynamicData, opts = {}) {
  const hrOrderCode = trimStr(opts.hrOrderCode) || resolveFlightsSuccessHrOrderCode(orderData, dynamicData);
  const bodies = collectOrderResponseBodies(orderData, dynamicData);
  const reservations = bodies.map(reservationFromBody).filter(Boolean);
  const flights = dynamicData?.flights || orderData?.flights || {};

  const pnr = firstReadableRef(
    [
      ...reservations.flatMap((res) => [
        res.supplierPNR,
        res.supplier_pnr,
        res.SupplierPNR,
        res.recordLocator,
        res.record_locator,
        res.pnr,
        res.PNR,
        res.bookingRef,
        res.booking_ref,
        res.supplierReservationNumber,
        res.supplier_reservation_number,
        res.reservationNumber,
        res.reservation_number,
      ]),
      ...bodies.flatMap((body) => [
        body.supplierPNR,
        body.supplier_pnr,
        body.recordLocator,
        body.pnr,
        body.PNR,
        body.bookingRef,
        body.supplierReservationNumber,
        body.reservationNumber,
      ]),
      flights.supplierPNR,
      flights.supplierPnr,
      orderData?.supplierPNR,
      dynamicData?.supplierPNR,
    ],
    hrOrderCode
  );

  const rawSabre = firstReadableRef(
    [
      ...reservations.flatMap((res) => [res.sabrePNR, res.sabrePnr, res.sabre_pnr, res.SabrePNR]),
      ...bodies.flatMap((body) => [body.sabrePNR, body.sabrePnr, body.sabre_pnr, body.SabrePNR]),
      flights.sabrePNR,
      flights.sabrePnr,
      orderData?.sabrePNR,
      dynamicData?.sabrePNR,
    ],
    hrOrderCode
  );

  const eTickets = collectTicketCandidates(
    reservations,
    bodies,
    flights,
    orderData,
    dynamicData
  ).filter((t) => t && t !== pnr && !sameAsHrCode(t, hrOrderCode));
  const ticketNumber = eTickets[0] || "";

  const airlinePnr = pnr && (isClassicAirlinePnr(pnr) || !isSupplierBookingReference(pnr)) ? pnr : "";
  const sabrePnr = rawSabre && isClassicAirlinePnr(rawSabre) ? rawSabre.toUpperCase() : "";
  const supplierBookingRef = firstNonEmpty(
    rawSabre && isSupplierBookingReference(rawSabre) ? rawSabre : "",
    !airlinePnr && pnr && isSupplierBookingReference(pnr) ? pnr : ""
  );

  let statusLabel = "ITINERARY";
  let statusTone = "itinerary";
  if (opts.isAwaitingPayment === true) {
    statusLabel = "ITINERARY";
    statusTone = "itinerary";
  } else if (eTickets.length) {
    statusLabel = "CONFIRMED · TICKETED";
    statusTone = "ticketed";
  } else if (airlinePnr || pnr) {
    statusLabel = "BOOKING CONFIRMED";
    statusTone = "confirmed";
  }

  const docketId = firstReadableRef(
    [
      ...reservations.flatMap((res) => [res.docketId, res.docket_id, res.DocketId, res.docketNumber]),
      ...bodies.flatMap((body) => [body.docketId, body.docket_id, body.DocketId, body.docketNumber]),
    ],
    hrOrderCode
  );

  const passenger = firstNonEmpty(
    leadPassengerFromRooms(dynamicData?.flights?.passengersinfo),
    leadPassengerFromRooms(orderData?.flights?.passengersinfo),
    leadPassengerFromRooms(orderData?.passengersinfo),
    leadPassengerFromRooms(dynamicData?.passengersinfo),
    opts.passengerFallback,
    orderData?.fullname,
    dynamicData?.fullname
  );

  const date = formatTicketIssueDate(
    firstNonEmpty(
      orderData?.created_at,
      orderData?.createdAt,
      dynamicData?.created_at,
      dynamicData?.createdAt,
      orderData?.date,
      dynamicData?.date,
      orderData?.checkin,
      orderData?.check_in,
      dynamicData?.check_in,
      dynamicData?.checkin
    )
  );

  const docketDistinct =
    docketId &&
    docketId !== pnr &&
    docketId !== ticketNumber &&
    docketId !== supplierBookingRef
      ? docketId
      : "";

  return {
    passenger,
    bookingCode: pnr || hrOrderCode,
    bookingCodeSource: pnr ? "pnr" : hrOrderCode ? "hr" : "",
    pnr,
    airlinePnr: airlinePnr || sabrePnr,
    sabrePnr,
    supplierBookingRef,
    docketId: docketDistinct,
    ticketNumber,
    eTickets,
    issuingOffice: trimStr(opts.issuingOffice),
    telephone: trimStr(opts.telephone),
    date,
    statusLabel,
    statusTone,
  };
}
